import React, { useRef, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import * as Yup from 'yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { emailRegexConstant, passwordRegexConstant, phoneRegexConstant } from '../../constants';
import { AlertType, APIResponse, LoginResponse } from '../../utilities/types';
import { loginApi } from '../../apis';
import { showSnackbar } from '../../store/slices/common.slice';
import { login } from '../../store/slices/auth.slice';
import { getApiErrorMessage } from '../../utilities/helper';
import CustomAlert from '../../components/elements/custom-alert.element';
import useNavigateHook from '../../hooks/navigate.hook';

const validationSchema = Yup.object().shape({
  username: Yup.string()
    .required('Email or phone number is required')
    .test('is-email-or-phone', 'Must be a valid email or 10-digit phone number', (value) => {
      if (!value) return false;
      return emailRegexConstant.test(value) || phoneRegexConstant.test(value);
    }),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required')
    .matches(
      passwordRegexConstant,
      'Password must contain at least one letter, one number and one special character'
    ),
});

const AdminLoginPage: React.FC = () => {
  const formikRef = useRef<FormikProps<{ username: string; password: string }> | null>(null);
  const dispatch = useDispatch();
  const { doNavigate } = useNavigateHook();
  // const [role, setRole] = useState('buyer');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // 👁️ state for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [alertMessage, showAlertMessage] = useState<{ message: string; type: AlertType }>({
    message: '',
    type: 'info',
  });

  const toggleAlertMessage = (message: string, type: AlertType) => {
    if (message === '') {
      return;
    }
    showAlertMessage({ message, type });
    setTimeout(() => {
      showAlertMessage({ message: '', type: 'info' });
    }, 4000);
  };

  const onsubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const response = (await loginApi(
        values.username,
        values.password
      )) as APIResponse<LoginResponse>;
      if (response.status) {
        const { user, token } = response.data;
        const userWithRequiredFields = {
          id: user.id,
          name: user.name,
          email: user.email,
          ...user,
        };
        dispatch(showSnackbar({ message: 'Logged in successfully', severity: 'success' }));
        dispatch(login({ user: { ...userWithRequiredFields }, token }));
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: { xs: '90%', sm: '500px' },
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          textAlign="center"
          fontWeight={600}
          fontSize="18px"
          letterSpacing="0.8px"
        >
          Login
        </Typography>
        <Formik
          innerRef={formikRef}
          initialValues={{
            username: '',
            password: '',
          }}
          validationSchema={validationSchema}
          validateOnMount
          onSubmit={(values) => {
            console.log('Form Submitted:', values);
          }}
        >
          {({ errors, touched, isValid, dirty, handleChange }) => (
            <Form>
              <Field
                as={TextField}
                name="username"
                label="Email or Phone *"
                placeholder="Enter your email or phone number"
                fullWidth
                margin="normal"
                onChange={handleChange}
                error={touched.username && Boolean(errors.username)}
                helperText={touched.username && errors.username}
              />

              <Field
                as={TextField}
                name="password"
                label="Password *"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                fullWidth
                margin="normal"
                onChange={handleChange}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ mt: 2, bgcolor: 'green', '&:hover': { bgcolor: 'darkgreen' } }}
                onClick={(e) => onsubmit(e)}
                disabled={!isValid || !dirty || isSubmitting}
              >
                Sign In
              </Button>

              {alertMessage.message !== '' && (
                <Box sx={{ my: 1 }}>
                  <CustomAlert type={alertMessage.type}>{alertMessage.message}</CustomAlert>
                </Box>
              )}

              <Button
                variant="text"
                sx={{ mt: 2, mx: 'auto', display: 'block', textAlign: 'center' }}
                onClick={() => doNavigate('/admin/reset-password')}
              >
                Forgot your password?
              </Button>
              <Button
                variant="text"
                color="info"
                sx={{
                  mt: 2,
                  mx: 'auto',
                  display: 'block',
                  textAlign: 'center',
                  textDecoration: 'underline',
                }}
                onClick={() => doNavigate('/')}
              >
                Go To Website
              </Button>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
};

export default AdminLoginPage;
